import Editor, { useMonaco } from "@monaco-editor/react";
import { useEffect, useRef, useState } from "react";

import { useTheme } from "../../../../context/ThemeContext";
import PythonHeader from "./PythonHeader";
import PythonOutput from "./PythonOutput";

const editorOptions = {
  minimap: { enabled: false },
  fontSize: 14,
  wordWrap: "off",
  lineNumbers: "on",
  scrollBeyondLastLine: false,
  smoothScrolling: true,
  renderLineHighlight: "all",
  tabSize: 2,
  formatOnType: true,
  automaticLayout: true,
  cursorStyle: "line",
  folding: true,
  suggestOnTriggerCharacters: true,
  quickSuggestions: { other: true, comments: true, strings: true },
  bracketPairColorization: { enabled: true },
  codeLens: true,
  links: true,
  matchBrackets: "always",
};

const PythonCodeEditor = ({
  isTeacher = false,
  initialCode,
  python,
  setPython,
  resetPythonCode = () => {},
  hasCodeContent = () => {},
  reRunCode = false,
}) => {
  const { theme } = useTheme();
  const [output, setOutput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isHorizontal, setIsHorizontal] = useState(true);
  const [abortRequested, setAbortRequested] = useState(false);
  const abortRef = useRef(false);

  const terminalRef = useRef(null);
  const resolveInputRef = useRef(null);
  const inputInProgress = useRef(false);
  const monaco = useMonaco();

  // THEME SETUP
  useEffect(() => {
    if (!monaco) return;

    monaco.editor.defineTheme("custom-light", {
      base: "vs",
      inherit: true,
      rules: [],
      colors: {
        "editor.background": "#ffffff",
        "editor.lineHighlightBackground": "#f0f0f0",
      },
    });

    monaco.editor.defineTheme("custom-dark", {
      base: "vs-dark",
      inherit: true,
      rules: [],
      colors: {
        "editor.background": "#1e272e",
        "editor.lineHighlightBackground": "#2a2d2e",
      },
    });

    monaco.editor.setTheme(theme === "dark" ? "custom-dark" : "custom-light");
  }, [monaco, theme]);

  // LOAD INITIAL CODE
  useEffect(() => {
    setPython(initialCode?.python || "");
    onRunCode(initialCode?.python);
  }, [initialCode?.python, reRunCode]);

  // OUTPUT HELPERS
  const outf = (text) => {
    if (!abortRequested) {
      setOutput((prev) => prev + text.replace(/</g, "&lt;") + "\n");
    }
  };

  const errorf = (err) => {
    if (!abortRequested) {
      setOutput((prev) => prev + "\n** Error **\n" + err.toString());
    }
  };

  const unblockInput = () => {
    const terminal = terminalRef.current;
    const inputEl = terminal?.querySelector("span[contenteditable=true]");
    if (inputEl) {
      const value = inputEl.innerText.trim();
      outf(value);
      terminal.removeChild(inputEl);
      resolveInputRef.current?.(value);
      resolveInputRef.current = null;
      inputInProgress.current = false;
    }
  };

  const inputf = () =>
    new Promise((resolve) => {
      if (abortRef.current || inputInProgress.current) return resolve("");

      inputInProgress.current = true;
      resolveInputRef.current = resolve;

      const terminal = terminalRef.current || document.getElementById("terminal");
      if (!terminal) return resolve("");

      const inputEl = document.createElement("span");
      Object.assign(inputEl.style, {
        display: "inline-block",
        color: "#1f1f1f",
        width: "100px",
        border: "1px solid #1f1f1f",
      });

      inputEl.contentEditable = true;
      inputEl.spellcheck = false;
      terminal.appendChild(inputEl);
      inputEl.focus();
      terminal.scrollTop = terminal.scrollHeight;

      const handleKeyDown = (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          const value = inputEl.innerText.trim();
          outf(value);
          inputEl.removeEventListener("keydown", handleKeyDown);
          terminal.removeChild(inputEl);
          inputInProgress.current = false;
          resolveInputRef.current = null;
          resolve(value);
        }
      };

      inputEl.addEventListener("keydown", handleKeyDown);

      // auto-abort after 30s
      setTimeout(() => {
        if (inputInProgress.current) {
          inputInProgress.current = false;
          inputEl.removeEventListener("keydown", handleKeyDown);
          terminal.removeChild(inputEl);
          outf("\n** Input timed out **");
          resolve("");
        }
      }, 30000);
    });

  const configureSkulpt = (code) => {
    setOutput("");
    Sk.canvas = "mycanvas";
    Sk.pre = "output";
    Sk.execLimit = 10000;

    Sk.configure({
      output: outf,
      inputfun: inputf,
      read: (x) => {
        if (!Sk.builtinFiles || !Sk.builtinFiles.files[x]) {
          throw `File not found: '${x}'`;
        }
        return Sk.builtinFiles.files[x];
      },
      __future__: Sk.python3,
    });

    return Sk.misceval.asyncToPromise(() => Sk.importMainWithBody("<stdin>", false, code, true));
  };

  const runSkulpt = async (code) => {
    abortRef.current = false;
    inputInProgress.current = false;
    setIsLoading(true);
    setIsRunning(true);
    setOutput("");

    Sk.canvas = "mycanvas";
    Sk.pre = "output";
    Sk.execLimit = 10000;

    Sk.configure({
      output: outf,
      inputfun: inputf,
      read: (x) => {
        if (!Sk.builtinFiles || !Sk.builtinFiles.files[x]) {
          throw `File not found: '${x}'`;
        }
        return Sk.builtinFiles.files[x];
      },
      __future__: Sk.python3,
    });

    try {
      const executionPromise = Sk.misceval.asyncToPromise(() =>
        Sk.importMainWithBody("<stdin>", false, code, true)
      );

      await Promise.race([
        executionPromise,
        new Promise((_, reject) => {
          const checkAbort = () => {
            if (abortRef.current) reject(new Error("Execution aborted by user"));
            else setTimeout(checkAbort, 100);
          };
          checkAbort();
        }),
      ]);
    } catch (err) {
      errorf(err.message || err.toString());
    } finally {
      setIsLoading(false);
      setIsRunning(false);
    }
  };

  const onRunCode = (codeParam) => {
    if (isRunning) {
      handleStop(); // stop current first
      setTimeout(() => runSkulpt(codeParam || python), 100); // rerun safely
    } else {
      runSkulpt(codeParam || python);
    }
  };

  const resetPythonCodeAndRunCode = () => {
    resetPythonCode();
    onRunCode("");
    setOutput("");
  };

  const handleStop = () => {
    abortRef.current = true;
    setIsRunning(false);
    setIsLoading(false);
    setOutput("** Execution interrupted **");

    // Clear pending input
    Sk.configure({ output: () => {} });
    unblockInput();
  };

  return (
    <div className="ltr-ignore">
      <PythonHeader
        onRunCode={onRunCode}
        onStopCode={handleStop}
        isLoading={isLoading}
        isRunning={isRunning}
        setIsHorizontal={setIsHorizontal}
        isHorizontal={isHorizontal}
        resetPythonCodeAndRunCode={resetPythonCodeAndRunCode}
        hasCodeContent={hasCodeContent}
        isTeacher={isTeacher}
      />

      <div className={`flex ${isHorizontal ? "gap-2" : "flex-col space-y-2"}`}>
        <div
          className={`w-full rounded-lg border border-gray-50 bg-white p-0 pb-4 shadow-md dark:border-dark-border dark:bg-[#1e272e] ${
            isHorizontal ? "lg:w-1/2" : ""
          }`}
        >
          <Editor
            options={editorOptions}
            height={isHorizontal ? "60vh" : "30vh"}
            theme={theme === "dark" ? "custom-dark" : "custom-light"}
            language="python"
            value={python}
            onChange={(value) => setPython(value)}
          />
        </div>

        <PythonOutput
          output={output}
          isRunning={isRunning}
          isHorizontal={isHorizontal}
          terminalRef={terminalRef}
        />
      </div>
    </div>
  );
};

export default PythonCodeEditor;
